import { ParrotLinkParams } from '@insurgrid/common';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { owlClient } from '../../apiUtils/client';
import { useAgentInfo } from './useAgentInfo';

export const ALL_LINES_FEATURE_FLAG = 'personal_commercial_link';
const ALL_LINES_VALUE = 'all';
const ALL_LINES_STORAGE_KEY = 'insurgrid_all_lines';

// Stores the agent slug, not a boolean, so the mode never carries over to another agent's link in the same tab.
function readStoredSlug(): string | null {
  try {
    return window.sessionStorage.getItem(ALL_LINES_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function useAllLinesMode() {
  const params = useParams();
  const agentSlug = params?.agentSlug as string | undefined;
  const query = useSearchParams();
  const linesParam = query?.get(ParrotLinkParams.Lines);
  const [storedSlug, setStoredSlug] = useState<string | null>(null);

  useEffect(() => {
    setStoredSlug(readStoredSlug());
  }, []);

  useEffect(() => {
    if (
      linesParam === ALL_LINES_VALUE &&
      agentSlug &&
      storedSlug !== agentSlug
    ) {
      try {
        window.sessionStorage.setItem(ALL_LINES_STORAGE_KEY, agentSlug);
      } catch {
        // sessionStorage can throw under strict privacy modes; the URL param still applies on this page.
      }
      setStoredSlug(agentSlug);
    }
  }, [linesParam, agentSlug, storedSlug]);

  const requested =
    !!agentSlug && (linesParam === ALL_LINES_VALUE || storedSlug === agentSlug);

  const { agentInfo, agentLoading } = useAgentInfo();
  const agencyId = agentInfo?.agencyId;

  const { data: flagEnabled, isLoading: flagLoading } = useQuery(
    ['agency_feature_flag', agencyId, ALL_LINES_FEATURE_FLAG],
    () =>
      owlClient.auth.getAgencyFeatureFlag({
        args: {
          agencyId: agencyId as number,
          featureName: ALL_LINES_FEATURE_FLAG,
        },
      }),
    {
      enabled: requested && !!agencyId,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  );

  return {
    allLines: requested && flagEnabled === true,
    allLinesLoading: requested && (agentLoading || (!!agencyId && flagLoading)),
  };
}
